import { useEffect, useState } from 'react';
import {
  connectMailchimp,
  disconnectMailchimp,
  downloadOptInsCsv,
  getNewsletter,
  listAudiences,
  listOptIns,
  newsletterError,
  syncNewsletter,
  type Audience,
  type MailchimpConnection,
  type OptIn,
} from '../../lib/newsletter-api';
import { StringListEditor } from './StringListEditor';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';
import { SecretInput } from './SecretInput';

const STATUS_LABEL: Record<NonNullable<OptIn['status']> | 'none', string> = {
  synced: 'In Mailchimp',
  pending: 'Sending…',
  failed: 'Failed',
  none: 'Not sent (no list connected)',
};

/**
 * Newsletter: the people who opted in from the chat (on the visitor form, or
 * by saying yes after the chat asked for their email), and the Mailchimp
 * audience they are added to. The opt-in is switched on under Visitor form &
 * newsletter; the list works without Mailchimp too (export it as CSV).
 */
const MAILCHIMP_KEY_RE = /^[0-9a-f]{32}-[a-z]{2,4}\d{1,3}$/i;

export function NewsletterSection({ siteId, optInOffered }: { siteId: string; optInOffered: boolean }) {
  const [conn, setConn] = useState<MailchimpConnection | null>(null);
  const [counts, setCounts] = useState({ total: 0, synced: 0, pending: 0, failed: 0 });
  const [optIns, setOptIns] = useState<OptIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [audiences, setAudiences] = useState<Audience[] | null>(null);
  const [audienceId, setAudienceId] = useState('');
  const [doubleOptIn, setDoubleOptIn] = useState(true);
  const [tags, setTags] = useState<string[]>(['chat']);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const [n, list] = await Promise.all([getNewsletter(siteId), listOptIns(siteId)]);
      setConn(n.mailchimp);
      setCounts(n.counts);
      setOptIns(list);
      if (n.mailchimp) {
        setAudienceId(n.mailchimp.audienceId);
        setDoubleOptIn(n.mailchimp.doubleOptIn);
        setTags(n.mailchimp.tags);
      }
    } catch {
      setError('Could not load the newsletter settings.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  async function run(label: string, fn: () => Promise<string | void>) {
    setBusy(label);
    setError(null);
    setNotice(null);
    try {
      const message = await fn();
      if (message) setNotice(message);
    } catch (err) {
      setError(newsletterError(err));
    } finally {
      setBusy(null);
    }
  }

  // Same shape the server checks (newsletter/mailchimp-rules.ts), so a wrong
  // value is caught here instead of after a round trip.
  const keyLooksWrong = apiKey.trim() !== '' && !MAILCHIMP_KEY_RE.test(apiKey.trim());

  if (loading) return <p className={styles.hint}>Loading…</p>;

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Newsletter</p>
      <p className={styles.hint} style={{ marginTop: 0 }}>
        Visitors can sign up for your newsletter from the chat: a checkbox on the visitor form, or a yes/no
        question after the chat collects their email. Connect a Mailchimp audience and they are added to it
        automatically; without one, export the list as CSV.
      </p>
      {!optInOffered && (
        <p className={styles.notice}>
          The sign-up isn’t offered to visitors yet. Switch it on under <strong>Visitor form &amp; newsletter</strong>.
        </p>
      )}

      <p className={styles.label} style={{ margin: 'var(--inic-space-4) 0 var(--inic-space-2)' }}>
        Mailchimp
      </p>
      {conn ? (
        <p className={styles.notice} role="status">
          {conn.enabled ? 'Connected' : 'Paused'} · audience <strong>{conn.audienceName ?? conn.audienceId}</strong>
          {conn.dataCenter ? ` (${conn.dataCenter})` : ''} · {conn.doubleOptIn ? 'double opt-in' : 'single opt-in'}
          {conn.tags.length ? ` · tags: ${conn.tags.join(', ')}` : ''}
        </p>
      ) : null}
      <div className={styles.form}>
        <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
          <span className={styles.label}>API key</span>
          <span style={{ display: 'flex', gap: 'var(--inic-space-2)' }}>
            <SecretInput
              wrapperStyle={{ flex: 1, minWidth: 0 }}
              value={apiKey}
              placeholder={conn ? '•••••••• (saved, leave blank to keep)' : '0123…abcd-us21'}
              aria-invalid={keyLooksWrong}
              aria-describedby="mailchimp-key-hint"
              onChange={(e) => setApiKey(e.target.value)}
            />
            <button
              type="button"
              className={styles.buttonGhost}
              disabled={busy !== null || keyLooksWrong || (!apiKey.trim() && !conn)}
              onClick={() =>
                run('audiences', async () => {
                  const list = await listAudiences(siteId, apiKey.trim() || undefined);
                  setAudiences(list);
                  if (!list.some((a) => a.id === audienceId)) setAudienceId(list[0]?.id ?? '');
                  return list.length ? undefined : 'This account has no audiences yet. Create one in Mailchimp first.';
                })
              }
            >
              {busy === 'audiences' ? 'Loading…' : 'Load audiences'}
            </button>
          </span>
          <span id="mailchimp-key-hint" className={keyLooksWrong ? styles.hintError : styles.hint}>
            {keyLooksWrong
              ? 'That isn’t a Mailchimp API key: it should be 32 letters and numbers ending in your data centre, like …-us21.'
              : 'Mailchimp → Profile → Extras → API keys. Stored encrypted; never shown again.'}
          </span>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Audience</span>
          {audiences ? (
            <select className={styles.select} value={audienceId} onChange={(e) => setAudienceId(e.target.value)}>
              {audiences.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.members} members)
                </option>
              ))}
            </select>
          ) : (
            <input
              className={styles.input}
              value={conn ? (conn.audienceName ?? conn.audienceId) : ''}
              placeholder="Load audiences first"
              readOnly
            />
          )}
        </label>
        <div className={styles.field}>
          <label className={styles.checkRow}>
            <input type="checkbox" checked={doubleOptIn} onChange={(e) => setDoubleOptIn(e.target.checked)} />
            <span className={styles.checkRowText}>
              <span className={styles.label} style={{ textTransform: 'none' }}>
                Double opt-in
              </span>
              <span className={styles.hint}>Mailchimp emails a confirmation link first (recommended).</span>
            </span>
          </label>
        </div>
        <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
          <span className={styles.label}>Tags added in Mailchimp</span>
          <StringListEditor
            value={tags}
            onChange={setTags}
            placeholder="chat"
            addLabel="Add tag"
            emptyText="No tags."
            max={10}
            maxLength={100}
          />
        </div>
        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.button}
            disabled={busy !== null || !audienceId || keyLooksWrong}
            onClick={() =>
              run('save', async () => {
                const next = await connectMailchimp(siteId, {
                  ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
                  audienceId,
                  doubleOptIn,
                  tags,
                  enabled: true,
                });
                setConn(next);
                setApiKey('');
                const pending = counts.total - counts.synced;
                return pending > 0
                  ? `Connected to ${next.audienceName}. ${pending} earlier sign-ups are not in it yet: use “Send to Mailchimp”.`
                  : `Connected to ${next.audienceName}.`;
              })
            }
          >
            {busy === 'save' ? 'Connecting…' : conn ? 'Save →' : 'Connect →'}
          </button>
          {conn && (
            <>
              <button
                type="button"
                className={styles.buttonGhost}
                disabled={busy !== null}
                onClick={() =>
                  run('sync', async () => {
                    const n = await syncNewsletter(siteId);
                    await load();
                    return n ? `Sending ${n} sign-ups to Mailchimp.` : 'Everyone is already in Mailchimp.';
                  })
                }
              >
                {busy === 'sync' ? 'Queuing…' : 'Send to Mailchimp'}
              </button>
              <button
                type="button"
                className={styles.buttonDanger}
                disabled={busy !== null}
                onClick={async () => {
                  if (!(await confirmDialog({ title: 'Disconnect Mailchimp?', message: 'Sign-ups are kept here; new ones won’t be sent.', confirmLabel: 'Disconnect' }))) return;
                  void run('disconnect', async () => {
                    await disconnectMailchimp(siteId);
                    setConn(null);
                    setAudiences(null);
                    return 'Disconnected.';
                  });
                }}
              >
                disconnect
              </button>
            </>
          )}
        </div>
      </div>
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      {error && <p className={styles.error} role="alert">{error}</p>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--inic-space-5)' }}>
        <p className={styles.label} style={{ margin: 0 }}>
          Sign-ups · {counts.total}
          {conn ? ` (${counts.synced} in Mailchimp${counts.failed ? `, ${counts.failed} failed` : ''})` : ''}
        </p>
        <button
          type="button"
          className={styles.buttonGhost}
          disabled={counts.total === 0 || busy !== null}
          onClick={() => run('csv', () => downloadOptInsCsv(siteId))}
        >
          Export CSV
        </button>
      </div>
      {optIns.length === 0 ? (
        <p className={styles.hint}>No sign-ups yet.</p>
      ) : (
        <div className={styles.tableWrap} style={{ marginTop: 'var(--inic-space-2)' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Signed up</th>
                <th>Mailchimp</th>
              </tr>
            </thead>
            <tbody>
              {optIns.map((o) => (
                <tr key={o.visitorId}>
                  <td className={styles.mono}>{o.email}</td>
                  <td>{o.name && o.name !== 'Guest' ? o.name : '—'}</td>
                  <td className={styles.mono}>{new Date(o.optInAt).toLocaleString()}</td>
                  <td title={o.error ?? undefined}>
                    {STATUS_LABEL[o.status ?? 'none']}
                    {o.error ? `: ${o.error}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
